import { PokemonCard } from '../types/pokemon';

// Durée de validité du cache en ms (5 minutes)
export const CACHE_TTL = 5 * 60 * 1000; 

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Classe générique pour gérer le cache
class CacheManager<T> {
  private cache: Map<string, CacheEntry<T>>;
  private ttl: number;

  constructor(ttl = CACHE_TTL) {
    this.cache = new Map();
    this.ttl = ttl;
  }

  // Récupérer une entrée du cache
  get(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    // Vérifier si l'entrée n'est pas expirée
    const now = Date.now();
    if (now - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  // Stocker une entrée dans le cache
  set(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Vérifier si une clé existe dans le cache et n'est pas expirée
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    const now = Date.now();
    if (now - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  // Supprimer une entrée du cache
  delete(key: string): void {
    this.cache.delete(key);
  }

  // Vider tout le cache
  clear(): void {
    this.cache.clear();
  }

  // Nettoyer les entrées expirées
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// Cache spécifique pour les cartes
export const cardsCache = new CacheManager<PokemonCard[]>();

// Cache pour les comptages de cartes
export const cardCountsCache = new CacheManager<Record<string, number>>();

// Fonction utilitaire pour générer une clé de cache pour les requêtes de cartes
export const generateCardsCacheKey = (params: Record<string, any>): string => {
  return JSON.stringify(params);
}; 